import type { GenericTrackingEvent, GtmEventEECproductClick } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { SearchProductCardClickTrackingItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardClickTrackingItemFragmentFragmentDoc';
import { SearchProductCardClickTrackingResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardClickTrackingResultFragmentFragmentDoc';
import { SearchProductCardClickTrackingVariationGroupFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardClickTrackingVariationGroupFragmentFragmentDoc';

/* GraphQL */ `
  fragment SearchProductCardClickTrackingResultFragment on SearchProductResult {
    categories {
      name
      isSelected
    }
    ... on ProperCategoryQueryResult {
      breadcrumbs {
        name
        pathName
      }
    }
  }
`;

/* GraphQL */ `
  fragment SearchProductCardClickTrackingItemFragment on StylePreviewItem {
    styleName
    brand {
      name
    }
    listPosition
  }
`;

/* GraphQL */ `
  fragment SearchProductCardClickTrackingVariationGroupFragment on CompleteVariationGroup {
    articleNumber
    price {
      currentPrice
    }
  }
`;

export const useProductClickTracking = (
  maskedResult: FragmentType<typeof SearchProductCardClickTrackingResultFragmentFragmentDoc>,
  maskedItem: FragmentType<typeof SearchProductCardClickTrackingItemFragmentFragmentDoc>,
  maskedVariationGroup: FragmentType<
    typeof SearchProductCardClickTrackingVariationGroupFragmentFragmentDoc
  >,
) => {
  const {
    brand: { name: brand },
    styleName,
    listPosition,
  } = getFragmentData(SearchProductCardClickTrackingItemFragmentFragmentDoc, maskedItem);
  const {
    articleNumber,
    price: { currentPrice },
  } = getFragmentData(
    SearchProductCardClickTrackingVariationGroupFragmentFragmentDoc,
    maskedVariationGroup,
  );
  const result = getFragmentData(
    SearchProductCardClickTrackingResultFragmentFragmentDoc,
    maskedResult,
  );

  const currentCategoryName = result.categories.find((category) => category.isSelected)?.name;

  const dispatchGTMEvent = useTracking();

  const customCategory =
    'breadcrumbs' in result && result.breadcrumbs
      ? result.breadcrumbs
          .filter((item) => item.pathName !== '/')
          .map(({ name }) => name)
          .join('/')
      : '';

  const listId = currentCategoryName ? `Category: ${currentCategoryName}` : 'Search Result';

  const dispatchProductClickTracking = () => {
    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `click=${listPosition};${articleNumber}`,
    });
    dispatchGTMEvent<GtmEventEECproductClick>({
      event: 'EECproductClick',
      ecommerce: {
        click: {
          actionField: {
            list: listId,
            action: 'click',
          },
          products: [
            {
              name: styleName,
              id: articleNumber,
              price: currentPrice / 100,
              category: customCategory,
              brand,
              position: listPosition ?? 0,
            },
          ],
        },
      },
    });
  };

  return {
    dispatchProductClickTracking,
  };
};
